'use client';

import { useCallback, useState } from 'react';
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DropzonePreview from './dropzone-preview';
import { compressImages } from '../../utils/compress-image';

type DropzoneAreaProps = {
  value: File[];
  onChange: (files: File[]) => void;
  accept?: Accept;
  maxSize?: number;
  maxFiles?: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
};

function dedupeFiles(existing: File[], incoming: File[]): File[] {
  const keys = new Set(
    existing.map((f) => `${f.name}:${f.size}:${f.lastModified}`),
  );
  return incoming.filter(
    (f) => !keys.has(`${f.name}:${f.size}:${f.lastModified}`),
  );
}

function formatRejectionError(rejection: FileRejection): string {
  const errors = rejection.errors.map((e) => e.message).join(', ');
  return `${rejection.file.name}: ${errors}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DropzoneArea = ({
  value,
  onChange,
  accept,
  maxSize,
  maxFiles,
  disabled,
  placeholder = 'Drag and drop files or click here',
  className,
}: DropzoneAreaProps) => {
  const [rejectionErrors, setRejectionErrors] = useState<string[]>([]);
  const [compressing, setCompressing] = useState(false);
  const [compressProgress, setCompressProgress] = useState(0);

  const isFull = Boolean(maxFiles && value.length >= maxFiles);

  const onDrop = useCallback(
    async (accepted: File[], rejections: FileRejection[]) => {
      setRejectionErrors(rejections.map(formatRejectionError));

      if (accepted.length === 0) return;

      const unique = dedupeFiles(value, accepted);
      if (unique.length === 0) return;

      setCompressing(true);
      setCompressProgress(0);
      const compressed = await compressImages(unique, setCompressProgress);
      setCompressing(false);

      let next = [...value, ...compressed];
      if (maxFiles && next.length > maxFiles) {
        next = next.slice(0, maxFiles);
      }
      onChange(next);
    },
    [value, onChange, maxFiles],
  );

  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles: maxFiles ? maxFiles - value.length : undefined,
    disabled: disabled || isFull || compressing,
    multiple: true,
  });

  const hasFiles = value.length > 0;
  const totalSize = value.reduce((sum, f) => sum + f.size, 0);

  return (
    <div className={className}>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 1,
          p: 2,
          cursor: disabled || isFull || compressing ? 'default' : 'pointer',
          bgcolor: isDragActive ? 'primary.background' : 'transparent',
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        <input {...getInputProps()} />

        {hasFiles ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {value.map((file, index) => (
                <DropzonePreview
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  file={file}
                  onRemove={() => handleRemove(index)}
                  disabled={disabled}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {value.length}{maxFiles ? ` of ${maxFiles}` : ''} file{value.length !== 1 ? 's' : ''} ({formatFileSize(totalSize)})
              </Typography>
              {!isFull && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AddPhotoAlternateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Add more
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 1 }}>
            <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {placeholder}
            </Typography>
            {maxSize && (
              <Typography variant="caption" color="text.secondary">
                Max {formatFileSize(maxSize)} per file
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {compressing && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress variant="determinate" value={compressProgress} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Compressing images…
          </Typography>
        </Box>
      )}

      {rejectionErrors.length > 0 && (
        <Alert
          severity="error"
          onClose={() => setRejectionErrors([])}
          sx={{ mt: 1 }}
        >
          {rejectionErrors.map((msg) => (
            <div key={msg}>{msg}</div>
          ))}
        </Alert>
      )}
    </div>
  );
};

export default DropzoneArea;
